// GitHub's native stacks are a public preview of its GraphQL schema: an
// older GitHub Enterprise Server has none of their fields, and refuses a
// document that names them. Every read that asks for them goes through
// `withStacks`, so the app learns it once and asks without them from then on.
import { GraphQLFailure, graphql, type GraphQLOptions } from './gh';

/** Set once GitHub's schema turned out to have no native stacks (older GitHub Enterprise Server). */
let noNativeStacks = false;

/** The native stack types; GitHub's schema may lack them, or some of their fields. */
const STACK_TYPES = new Set(['PullRequestStack', 'PullRequestStackEntry', 'PullRequestStackEntryConnection']);

/** Whether GitHub refused the query for naming native stack fields its schema lacks. */
function lacksNativeStacks(e: unknown): boolean {
  if (!(e instanceof GraphQLFailure)) return false;
  return e.errors.some((x) => {
    if (x.extensions?.code !== 'undefinedField') return false;
    const { typeName, fieldName } = x.extensions;
    return (
      (typeName === 'PullRequest' && (fieldName === 'stack' || fieldName === 'stackEntry')) ||
      STACK_TYPES.has(String(typeName))
    );
  });
}

/**
 * Run `stacked`, or `bare` (the same document without native stack fields)
 * where GitHub's schema has no native stacks: once a query is refused for
 * naming them, it is sent again without them, and so is every query from
 * then on.
 */
export async function withStacks<T>(stacked: string, bare: string, variables: Record<string, unknown>, opts: GraphQLOptions): Promise<T> {
  if (noNativeStacks) return graphql<T>(bare, variables, opts);
  try {
    return await graphql<T>(stacked, variables, opts);
  } catch (e) {
    if (!lacksNativeStacks(e)) throw e;
    noNativeStacks = true;
    return graphql<T>(bare, variables, opts);
  }
}
